'use client';

import { Button } from '@/shared/ui/shadcn/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/ui/shadcn/tooltip';
import { MemoizedMarkdown } from '@/shared/ui/primitives';
import { cn } from '@/lib/utils';
import type { UIMessage } from 'ai';
import type { ComponentProps, HTMLAttributes } from 'react';
import { memo, useId } from 'react';

export type MessageProps = HTMLAttributes<HTMLDivElement> & {
  from: UIMessage['role'];
};

export const Message = ({ className, from, ...props }: MessageProps) => (
  <div
    className={cn(
      'group flex w-full flex-col gap-2',
      from === 'user' ? 'is-user' : 'is-assistant',
      className
    )}
    {...props}
  />
);

export type MessageContentProps = HTMLAttributes<HTMLDivElement>;

export const MessageContent = ({ children, className, ...props }: MessageContentProps) => (
  <div
    className={cn(
      'is-user:dark flex w-fit max-w-full min-w-0 flex-col gap-2 overflow-hidden text-sm font-medium',
      // A full-width card echoing the composer, like an editor's agent sidebar.
      'group-[.is-user]:w-full group-[.is-user]:rounded-xl group-[.is-user]:border group-[.is-user]:border-border group-[.is-user]:bg-surface-sunken group-[.is-user]:px-3 group-[.is-user]:py-2.5 group-[.is-user]:[overflow-wrap:anywhere] group-[.is-user]:text-foreground',
      'group-[.is-assistant]:text-foreground',
      className
    )}
    {...props}
  >
    {children}
  </div>
);

export type MessageActionsProps = ComponentProps<'div'>;

export const MessageActions = ({ className, children, ...props }: MessageActionsProps) => (
  <div className={cn('flex items-center gap-1', className)} {...props}>
    {children}
  </div>
);

export type MessageActionProps = ComponentProps<typeof Button> & {
  tooltip?: string;
  label?: string;
};

export const MessageAction = ({
  tooltip,
  children,
  label,
  variant = 'ghost',
  size = 'icon-sm',
  className,
  ...props
}: MessageActionProps) => {
  const button = (
    <Button
      className={cn('h-6 w-6 [&_svg]:size-3', className)}
      size={size}
      type="button"
      variant={variant}
      {...props}
    >
      {children}
      <span className="sr-only">{label || tooltip}</span>
    </Button>
  );

  if (tooltip) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>{button}</TooltipTrigger>
        <TooltipContent>
          <p>{tooltip}</p>
        </TooltipContent>
      </Tooltip>
    );
  }

  return button;
};

export type MessageResponseProps = {
  className?: string;
  children: string;
  streamdownProps?: ComponentProps<typeof MemoizedMarkdown>['streamdownProps'];
};

export const MessageResponse = memo(
  ({ className, children, streamdownProps }: MessageResponseProps) => {
    const id = useId();
    return (
      <MemoizedMarkdown
        className={cn(
          'size-full text-sm font-medium [&>*:first-child]:mt-0 [&>*:last-child]:mb-0',
          className
        )}
        content={children}
        id={id}
        streamdownProps={{ controls: { table: false }, ...streamdownProps }}
      />
    );
  },
  (prevProps, nextProps) =>
    prevProps.children === nextProps.children &&
    prevProps.className === nextProps.className &&
    prevProps.streamdownProps === nextProps.streamdownProps
);

MessageResponse.displayName = 'MessageResponse';

export type MessageToolbarProps = ComponentProps<'div'>;

export const MessageToolbar = ({ className, children, ...props }: MessageToolbarProps) => (
  <div className={cn('mt-4 flex w-full items-center justify-between gap-4', className)} {...props}>
    {children}
  </div>
);
